import { useState, useMemo } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { TicketsTable } from '@/components/tickets/TicketsTable';
import { TicketFilters } from '@/components/tickets/TicketFilters';
import { TicketPagination } from '@/components/tickets/TicketPagination';
import { useTickets } from '@/hooks/tickets';
import type { SortingState } from '@tanstack/react-table';
import type { TicketStatus, TicketCategory } from '@/components/tickets/types';

const PAGE_SIZE = 10;
const SORTABLE_COLUMNS = new Set(['subject', 'status', 'createdAt', 'category', 'assignedAgent']);

export default function TicketsPage() {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'ALL'>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<TicketCategory | 'ALL'>('ALL');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const sortCol = sorting[0];
  const sortBy = sortCol && SORTABLE_COLUMNS.has(sortCol.id) ? sortCol.id : 'createdAt';
  const sortOrder = sortCol?.desc === false ? 'asc' : 'desc';

  const { data, isLoading, isError } = useTickets({
    sortBy,
    sortOrder,
    statusFilter,
    categoryFilter,
    page,
    pageSize: PAGE_SIZE,
  });

  const tickets = useMemo(() => {
    const all = data?.tickets ?? [];
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(
      (t) =>
        t.subject.toLowerCase().includes(q) ||
        t.fromName.toLowerCase().includes(q) ||
        t.fromEmail.toLowerCase().includes(q),
    );
  }, [data?.tickets, search]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function resetPage<T>(setter: (v: T) => void) {
    return (v: T) => {
      setter(v);
      setPage(1);
    };
  }

  return (
    <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto flex flex-col gap-6'>
      <div>
        <p className='text-[11px] font-bold tracking-[0.15em] uppercase mb-1' style={{ color: 'oklch(0.65 0.22 285)' }}>
          Support
        </p>
        <h1 className='text-2xl font-bold font-syne'>Tickets</h1>
      </div>

      <TicketFilters
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusChange={resetPage(setStatusFilter)}
        categoryFilter={categoryFilter}
        onCategoryChange={resetPage(setCategoryFilter)}
      />

      {isError && (
        <Alert variant='destructive'>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>Failed to load tickets.</AlertDescription>
        </Alert>
      )}

      <TicketsTable
        tickets={tickets}
        isLoading={isLoading}
        sorting={sorting}
        onSortingChange={(updater) => {
          setSorting(updater);
          setPage(1);
        }}
      />

      {!isLoading && (
        <TicketPagination
          page={page}
          totalPages={totalPages}
          total={total}
          onPageChange={setPage}
        />
      )}
    </div>
  );
}
